import { ActionIcon, Modal } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import type { ReactElement } from 'react'
import type { RecordColumn } from '../SelectRecord'
import { SelectPatient, type SelectPatientRow } from './SelectPatient'

type SelectPatientModalProps = {
  opened: boolean
  onClose: () => void
  onSelect: (patient: SelectPatientRow) => void
  // Close the modal automatically once a patient is picked. Default true.
  closeOnSelect?: boolean
  pageSize?: number
  columns?: RecordColumn<SelectPatientRow>[]
  // Optional search hint used to pre-seed the picker's global search on open.
  mask?: string
}

/**
 * Modal-dialog presentation of {@link SelectPatient}. The dialog gives the picker a definite
 * viewport-relative height so the fill-all-space table has room to scroll; SelectPatient renders its
 * own "Select Patient" title bar, so the Mantine header is suppressed and a close button floats in the
 * title bar's (otherwise empty) right slot.
 */
export function SelectPatientModal({
  opened,
  onClose,
  onSelect,
  closeOnSelect = true,
  pageSize,
  columns,
  mask,
}: SelectPatientModalProps): ReactElement {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      // We own Escape (via the wrapper's onKeyDown below) instead of Mantine's window-level handler:
      // a non-empty filter/search field consumes Escape to clear itself; anything else bubbles here
      // and closes the dialog. Mantine's own Escape close is disabled so it can't fire on a clear.
      closeOnEscape={false}
      padding={0}
      size="95rem"
      centered
      radius="md"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      styles={{
        content: { height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        body: { flex: 1, minHeight: 0, padding: 0 },
      }}
    >
      <div className="relative h-full w-full">
        <SelectPatient
          pageSize={pageSize}
          columns={columns}
          mask={mask}
          onSelect={(patient) => {
            onSelect(patient)
            if (closeOnSelect) onClose()
          }}
          // Escape not consumed by a field clear closes the dialog (handled generically in
          // SelectRecord).
          onEscape={onClose}
        />
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-4 z-30"
        >
          <IconX size={18} />
        </ActionIcon>
      </div>
    </Modal>
  )
}

export type { SelectPatientModalProps }
